import {useComputed, useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import moment from 'moment'
import {Button} from 'primereact/button'
import {Tag} from 'primereact/tag'
import {Timeline} from 'primereact/timeline'
import {useEffect, useMemo, useRef} from 'react'
import {ReactComponent as CalendarIcon} from '../../assets/icons/CalendarIcon.svg'
import scheduleImg from '../../assets/images/schedule-undraw.svg'
import {SystemRole} from '../../Constants'
import {useAppConfig} from '../../context/app/AppContext'
import {useUserHospital} from '../../context/hospital/UserHospitalContext'
import {getLanguageCode} from '../../i18n/LanguageUtils'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyManager} from '../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {useAuth} from '../authentication/AuthProvider'
import {getClientId} from '../authentication/AuthUtils'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {
    calendarItemEvent,
    calendarItemEventTypes,
    getCalendarItemEventKey,
} from '../styled-component/reservations/broadcast/CalendarItemEvent'
import CalendarForm from './CalendarForm'
import {
    useAppointmentListTopBar,
    useReminderUpdateStatus,
} from './query-keys/ReminderKeysManager'
import './reminder.css'
import {reminderStatus} from './ReminderConstants'
const appointmentTypes = {
    today: 'today',
    upcoming: 'upcoming',
    missed: 'missed',
    done: 'done',
}

const missedTypes = {
    none: 'none',
    missed: 'missed',
    pending: 'pending',
}

const missedMaxHours = 2

// Used to display whether the appointment has been missed, and if not, set the remaining time
const TimeRemaining = ({startDateString}) => {
    useSignals()

    const missedValue = useSignal(null)
    const currentTime = useRef(null)
    const {translateCommons} = TranslateUtils()
    const {timezoneId} = useAppConfig()

    useEffect(() => {
        if (!startDateString) {
            return
        }

        let timeout = 0
        let startDateTime = new Date(startDateString)
        let remainingTime = new Date(
            new Date(startDateTime).setHours(
                startDateTime.getHours() + missedMaxHours,
            ),
        )

        const timerId = setInterval(() => {
            timeout = remainingTime.getTime() - Date.now() // Format Current Date Time to Client TZ for compare

            if (timeout < 0) {
                missedValue.value = missedTypes.missed
                return
            }

            // Time calculations for days, hours, minutes and seconds
            const hours = Math.floor(
                (timeout % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
            )

            if (hours <= 2) {
                const minutes = Math.floor(
                    (timeout % (1000 * 60 * 60)) / (1000 * 60),
                )
                const seconds = Math.floor((timeout % (1000 * 60)) / 1000)

                if (
                    (!!hours || hours === 0) &&
                    (!!minutes || minutes === 0) &&
                    (!!seconds || seconds === 0)
                ) {
                    if (currentTime.current) {
                        currentTime.current.innerText = `${translateCommons(
                            'Pending time ',
                        )} (${hours}h ${minutes}m ${seconds}s)`
                    }
                    missedValue.value = missedTypes.pending
                }
            } else {
                missedValue.value = missedTypes.none
            }
        }, 1000)

        return () => clearInterval(timerId)
    }, [missedValue, startDateString, timezoneId, translateCommons])

    return (
        <>
            {missedValue.value &&
                (missedValue.value === missedTypes.missed ? (
                    <Tag severity="danger" value="Missed"></Tag>
                ) : (
                    <div
                        className="font-bold text-500 select-none m-0"
                        ref={currentTime}></div>
                ))}
        </>
    )
}

const ReminderTabView = () => {
    useSignals()

    const {userRole} = useAppConfig()
    const {currentHospital} = useUserHospital()
    const {timezoneId} = useAppConfig()
    const {translateCommons} = TranslateUtils()
    const activeIndex = useSignal(0)
    const {user} = useAuth()
    const reminderFormRef = useRef()

    const activeType = useComputed(() => {
        let type = appointmentTypes.today
        switch (activeIndex.value) {
            case 0:
                type = appointmentTypes.today
                break
            case 1:
                type = appointmentTypes.upcoming
                break
            case 2:
                type = appointmentTypes.done
                break
            case 3:
                type = appointmentTypes.missed
                break
            default:
                type = appointmentTypes.today
                break
        }
        return type
    })
    const queryClient = useQueryClient()
    const {data: appointmentData} = useAppointmentListTopBar(
        user?.id,
        userRole === SystemRole.USER_WITH_ROLE ? currentHospital?.id : null,
        appointmentTypes.today,
    )

    // Date Format for filter calendar
    const dataFormated = useMemo(() => {
        if (appointmentData && appointmentData?.length) {
            let holderList = []
            for (let calendarItem of appointmentData) {
                let clientTZ = moment.tz(
                    moment(calendarItem?.start).format('YYYY-MM-DD HH:mm'),
                    'YYYY-MM-DD HH:mm',
                    timezoneId,
                )
                let UTC = moment.utc(clientTZ)
                let currentTZ = moment.tz(
                    UTC,
                    Intl.DateTimeFormat().resolvedOptions().timeZone,
                )

                holderList.push({
                    ...calendarItem,
                    start: currentTZ.format('YYYY-MM-DD HH:mm'),
                    end: currentTZ.format('YYYY-MM-DD HH:mm'),
                })
            }

            return holderList
        }

        return []
    }, [appointmentData, timezoneId])

    const {mutateAsync: updateStatus} = useReminderUpdateStatus(user?.id)

    const handleUpdateStatus = async (data, status) => {
        let toPostData = {...data, status}
        const responseData = await updateStatus(toPostData)
        if (responseData?.id) {
            const {reminderKeys} = QueryKeyManager(
                QueryKeyTypes.reminder,
                user?.id,
            )

            queryClient.invalidateQueries(reminderKeys.pattern())
        }
    }

    const tabPanelList = [
        {header: 'Today'},
        // {header: 'Upcoming'},
        // {header: 'Done'},
        // {header: 'Missed'},
    ]

    // const appointmentList = () => {
    //     let holderList = []

    //     const groupByHour = data => {
    //         const grouped = {}

    //         data.forEach(item => {
    //             const hour = moment(item.start).format('HH:00')

    //             if (!grouped[hour]) {
    //                 grouped[hour] = []
    //             }

    //             grouped[hour].push(item)
    //         })

    //         const now = moment()

    //         return Object.keys(grouped).map(hour => {
    //             const hourMoment = moment(hour, 'HH:00')
    //             const hourEnd = hourMoment.clone().endOf('hour')

    //             return {
    //                 time: hour,
    //                 items: grouped[hour],
    //                 isOverdue: now.isAfter(hourEnd),
    //             }
    //         })
    //     }

    //     holderList = groupByHour(appointmentData)

    //     return holderList && holderList.length > 0 ? holderList : []
    // }

    const appointmentList = useMemo(() => {
        let holderList = []

        if (isArrayAndNotEmpty(dataFormated)) {
            // sort
            let sortTimeList = dataFormated.sort((a, b) => {
                return moment(a.start).diff(moment(b.start))
            })

            // Group items by date and time
            const groupedData = sortTimeList.reduce((acc, item) => {
                const date = moment(item.start).format('YYYY-MM-DD')
                const time = moment(item.start).format('HH:00')

                // Ensure the date group exists
                if (!acc[date]) {
                    acc[date] = {groupByDate: date, groupItem: []}
                }

                // Ensure the time group exists within the date group
                const dateGroup = acc[date]
                const timeGroup = dateGroup.groupItem.find(
                    g => g.groupTime === time,
                )

                if (!timeGroup) {
                    dateGroup.groupItem.push({
                        groupTime: time,
                        items: [item],
                    })
                } else {
                    timeGroup.items.push(item)
                }

                return acc
            }, {})

            // Convert to desired format
            const resultData = Object.values(groupedData).map(dateGroup => {
                const itemsByTime = dateGroup.groupItem.flatMap(timeGroup => [
                    {groupTime: timeGroup.groupTime},
                    ...timeGroup.items.map(item => ({
                        start: item.start,
                        ...item,
                    })),
                ])

                return {
                    groupByDate: dateGroup.groupByDate,
                    groupItem: itemsByTime,
                }
            })
            if (isArrayAndNotEmpty(resultData)) {
                holderList = resultData
            }
        }

        return holderList && holderList.length > 0 ? holderList : []
    }, [dataFormated])

    const timeStatus = {past: 'past', now: 'now', notYet: 'notYet'}

    const getCurrentTimeItemList = (data = [], isOverdue = false) => {
        let holderList = []
        if (data && Array.isArray(data) && data.length > 0) {
            for (let i = 0; i < data.length; i++) {
                let item = {...data[i], timeStatus: timeStatus.notYet}

                if (item.start) {
                    const currentTime = moment()
                    const startTime = moment(item.start)
                    item.timeStatus = currentTime.isAfter(startTime)
                        ? timeStatus.past
                        : timeStatus.notYet
                }

                holderList.push(item)
            }
        }
        return holderList
    }

    const iconStyle = {
        color: 'darkcyan',
        width: '17px',
        height: '17px',
    }

    const isMissedAppointment = startDateString => {
        let startDateTime = new Date(startDateString)
        let remainingTime = new Date(
            new Date(startDateTime).setHours(
                startDateTime.getHours() + missedMaxHours,
            ),
        )
        if (Date.now() >= remainingTime.getTime()) {
            return missedTypes.missed
        } else if (Date.now() >= startDateTime.getTime()) {
            return missedTypes.pending
        } else {
            return missedTypes.none
        }
    }

    const contentTimeLineTemplate = item => {
        if (item.groupTime) {
            return
        }
        const missedType = isMissedAppointment(item.start)

        return (
            <div className="card border-1 border-gray-200 p-2 py-3 hover:bg-gray-50 w-full">
                <div className="flex flex-column flex-wrap mb-3 p-2">
                    <div className="flex align-items-center justify-content-between">
                        <h4 className="font-bold mb-0">
                            {item?.appointmentType}
                        </h4>
                        {missedType !== missedTypes.none ? (
                            <TimeRemaining startDateString={item.start} />
                        ) : (
                            <Tag severity="info" value="Up coming"></Tag>
                        )}
                    </div>
                    <p className="m-0 mb-2 p-0 text-primary font-bold">
                        {moment(item.start).format('HH:mm A')}
                    </p>
                    <div className="flex align-items-center">
                        <p className="mr-2 m-0 text-600 font-semibold ">
                            Patient Name:
                        </p>

                        <h6 className="font-bold mr-2 m-0 p-0">{item.name}</h6>
                        {/* <Avatar
                            className=""
                            label={
                                !item.avatar && item.name
                                    ? item.name.charAt(0)
                                    : ''
                            }
                            shape="circle"
                            image={item.avatar}
                            size={'medium'}
                            style={{
                                color: 'darkcyan',
                            }}
                        /> */}
                    </div>
                    <div className="flex flex-wrap align-items-center">
                        <p className="m-0 text-600 font-semibold mr-2">
                            Description:
                        </p>
                        <p className="m-0 capitalized white-space-normal">
                            {item.description}
                        </p>
                    </div>
                </div>
                <div className="flex align-items-center justify-content-end gap-2">
                    {activeType.value === appointmentTypes.done &&
                        item?.updatedAt && (
                            <p className="font-bold text-darkcyan">
                                <span className="mr-2">
                                    <i className="pi pi-check-circle"></i>
                                </span>
                                Done at{' '}
                                {item?.updatedAt ? item?.updatedAt : '-'}
                            </p>
                        )}

                    <Button
                        className="text-sm px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-500 border-blue-300"
                        icon="pi pi-calendar-minus"
                        label="Reschedule"
                        onClick={() => {
                            const appointmentItem = appointmentData?.find(
                                appointment => appointment?.id === item?.id,
                            )

                            reminderFormRef.current.showForm(appointmentItem)
                        }}
                    />

                    {missedType !== missedTypes.missed && (
                        <Button
                            className="text-sm px-5 py-2 p-button-outlined"
                            icon="pi pi-check-circle"
                            label="Done"
                            onClick={() =>
                                handleUpdateStatus(item, reminderStatus.done)
                            }
                        />
                    )}
                </div>
            </div>
        )
    }

    //#region Event Listener on State Changes
    useEffect(() => {
        // CALENDAR ITEM CHANGE
        calendarItemEvent.on(
            getCalendarItemEventKey(calendarItemEventTypes.TABLE_CHANGE),
            data => {
                if (
                    !data ||
                    !data?.clientId ||
                    data?.clientId !== getClientId()
                ) {
                    return
                }

                if (!data?.ownerId || user?.id !== data?.ownerId) {
                    return
                }

                const {reminderKeys} = QueryKeyManager(
                    QueryKeyTypes.reminder,
                    user?.id,
                )

                queryClient.invalidateQueries({
                    queryKey: reminderKeys.pattern(),
                })
            },
        )
    }, [])

    return (
        <>
            <div className="flex align-items-center">
                <div className="mr-2">
                    <CalendarIcon style={{fontSize: '26px'}} />
                </div>
                <h3 className="font-bold m-0">
                    {' '}
                    {translateCommons("Today's appointments")}
                </h3>
            </div>
            <div className="mb-2">
                <p>
                    {translateCommons(
                        'The following are the appointments for today',
                    )}
                </p>
            </div>

            <div className="overflow-scroll mt-3" style={{height: '35rem'}}>
                <p className="mb-2 font-bold text-xl text-primary">
                    {moment()
                        .locale(
                            getLanguageCode() === 'gb'
                                ? 'en'
                                : getLanguageCode(),
                        )
                        .format('MMMM Do YYYY')}
                </p>
                {isArrayAndNotEmpty(appointmentList) ? (
                    appointmentList.map((group, groupIdx) => (
                        <div key={groupIdx}>
                            <div>
                                <Timeline
                                    className="custom-appointment-timeline"
                                    value={group.groupItem}
                                    opposite={item => (
                                        <div className="w-3rem font-semibold">
                                            {item.groupTime}
                                        </div>
                                    )}
                                    content={contentTimeLineTemplate}
                                />
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="flex justify-content-center">
                        {' '}
                        <div className="w-30rem text-center">
                            <img
                                alt="no-appointments"
                                className="w-full p-4"
                                src={scheduleImg}
                                style={{
                                    height: '20rem',
                                    widht: '20rem',
                                }}
                            />
                            <h6 className="font-bold mb-2">
                                {translateCommons('There are no appointments')}{' '}
                                {translateCommons(activeType.value)}.
                            </h6>
                            <p>
                                {translateCommons(
                                    'Seem like you have no appointments',
                                )}{' '}
                                {translateCommons(activeType.value)}.
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Form Ref */}
            <CalendarForm ref={reminderFormRef} />
        </>
    )
}

export default ReminderTabView
